import React, { useState, useMemo, useCallback } from 'react';
import {
  Button,
  Body,
  MonoLabel,
  Badge,
  Checkbox,
  InlineMessage,
} from '@/components/rc';
import type { ParsedWine } from '@/utils/importDedup';
import { getPurchaseProvider } from '@/utils/purchaseAvailability';
import { cn } from '@/lib/utils';

// ── Types ──

interface BottleSelectGateProps {
  wines: ParsedWine[];
  remainingCapacity: number;
  totalExistingBottles: number;
  onConfirm: (selectedWines: ParsedWine[]) => void;
  onUpgrade: () => void;
  /** True while a Play purchase is in flight or being confirmed — Stripe's
   * redirect leaves no time for this screen to show a loading state at all,
   * so this only ever applies on that path. */
  upgrading?: boolean;
}

// ── Component ──

export default function BottleSelectGate({
  wines,
  remainingCapacity,
  totalExistingBottles,
  onConfirm,
  onUpgrade,
  upgrading = false,
}: BottleSelectGateProps) {
  // See utils/purchaseAvailability — without a purchase path the gate becomes
  // a plain "pick what fits" screen.
  const canPurchase = getPurchaseProvider() !== null;
  const csvBottles = useMemo(
    () => wines.reduce((sum, w) => sum + w.quantity, 0),
    [wines],
  );

  // Track selected indices (all selected by default up to capacity)
  const [selected, setSelected] = useState<Set<number>>(() => {
    const set = new Set<number>();
    let count = 0;
    for (let i = 0; i < wines.length; i++) {
      if (count + wines[i].quantity <= remainingCapacity) {
        set.add(i);
        count += wines[i].quantity;
      }
    }
    return set;
  });

  const selectedBottles = useMemo(
    () => wines.reduce((sum, w, i) => sum + (selected.has(i) ? w.quantity : 0), 0),
    [wines, selected],
  );

  const overCap = selectedBottles > remainingCapacity;
  const pct = remainingCapacity > 0
    ? Math.min(100, (selectedBottles / remainingCapacity) * 100)
    : selectedBottles > 0 ? 100 : 0;

  const toggleWine = useCallback((index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(wines.map((_, i) => i)));
  }, [wines]);

  const deselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  // With no capacity left and no way to buy more from here, the selection UI
  // is unwinnable: choosing nothing leaves the confirm button disabled, and
  // choosing anything puts it over capacity. Say so instead of presenting a
  // screen the user cannot complete.
  if (!canPurchase && remainingCapacity === 0) {
    return (
      <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <InlineMessage
          tone="warning"
          message="Your cellar is full"
          secondaryMessage={`Free accounts hold up to 50 bottles, and you have ${totalExistingBottles}. Rave Cave Reserve removes the limit.`}
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* Section A — capacity banner */}
      <div className="mb-4">
        <InlineMessage
          tone="warning"
          message="Import exceeds free tier"
          secondaryMessage={`Your file has ${csvBottles} bottles but you only have ${remainingCapacity} slots remaining (${totalExistingBottles} of 50 used).`}
        />
      </div>

      <div className="mb-4">
        <Body size="caption" colour="secondary" className="w-auto">
          {canPurchase
            ? 'Select which wines to import, or upgrade for unlimited space.'
            : 'Select which wines to import.'}
        </Body>
      </div>

      {/* Bulk actions */}
      <div className="flex gap-3 mb-3">
        <button
          onClick={selectAll}
          className="font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)] transition-colors"
        >
          Select all
        </button>
        <button
          onClick={deselectAll}
          className="font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)] transition-colors"
        >
          Deselect all
        </button>
      </div>

      {/* Section B — Wine selection list */}
      <div>
        {wines.map((wine, idx) => (
          <WineRow
            key={`${wine.lineIndex}-${idx}`}
            wine={wine}
            checked={selected.has(idx)}
            onToggle={() => toggleWine(idx)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-[var(--rc-border-subtle)] pt-3 mt-2">
        {/* Progress bar */}
        <div className="h-2 rounded-full bg-[var(--rc-surface-tertiary)] overflow-hidden mb-2">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-200',
              overCap
                ? 'bg-[var(--rc-accent-coral)]'
                : 'bg-[var(--rc-accent-pink)]',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Counter */}
        <div role="status" aria-live="polite">
          <MonoLabel
            size="label"
            colour={overCap ? 'accent-coral' : 'ghost'}
            className="w-auto"
          >
            {selectedBottles} / {remainingCapacity} bottles selected
            {overCap && ' — over capacity'}
          </MonoLabel>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mt-3">
          {canPurchase && (
            <Button
              variantType="Primary"
              label={upgrading ? 'Confirming...' : 'Upgrade to add all wines'}
              onClick={onUpgrade}
              disabled={upgrading}
              className="flex-1"
            />
          )}
          <Button
            variantType={canPurchase ? 'Secondary' : 'Primary'}
            label={`Continue with ${selectedBottles}`}
            disabled={overCap || selectedBottles === 0 || upgrading}
            onClick={() => {
              const selectedWines = wines.filter((_, i) => selected.has(i));
              onConfirm(selectedWines);
            }}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
}

// ── Wine row ──

function WineRow({
  wine,
  checked,
  onToggle,
}: {
  wine: ParsedWine;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      className="border-b border-[var(--rc-border-subtle)] last:border-0 cursor-pointer py-2"
      style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '48px', width: '100%' }}
    >
      <div style={{ flexShrink: 0 }}>
        <Checkbox
          variant={checked ? 'Checked' : 'Unchecked'}
          onChange={onToggle}
        />
      </div>
      <div style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: "'Instrument Sans', sans-serif",
            color: 'var(--rc-ink-primary)',
          }}
        >
          {wine.producer && wine.name
            ? `${wine.producer} ${wine.name}`
            : wine.name || wine.producer}
        </div>
        {(wine.vintage || wine.cepage) && (
          <MonoLabel size="micro" colour="ghost" className="w-auto mt-0.5">
            {[wine.vintage, wine.cepage].filter(Boolean).join(' \u00B7 ')}
          </MonoLabel>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        <Badge typeVariant="Count" tone="Neutral" label={wine.quantity} />
      </div>
    </div>
  );
}
